import { AdditiveBlending, DoubleSide, DynamicDrawUsage, InstancedInterleavedBuffer, MeshBasicNodeMaterial, NormalBlending, SpriteNodeMaterial, } from 'three/webgpu';
import { attribute, cameraPosition, cross, dot, float, fract, instancedDynamicBufferAttribute, mix, modelViewMatrix, modelWorldMatrix, positionGeometry, step, texture, uv, vec2, vec3, vec4, } from 'three/tsl';
import { aliveFactor, billboard, crossFalloff, normalizedAge, radialFalloff, sampleCurve, sampleGradient, } from './tsl/lib.js';
import { builtinSprite, resolveSpriteTexture } from './sprites.js';
import { compileGraph } from './graph.js';
/**
 * Per-particle data, interleaved into ONE instanced buffer.
 *
 * Why interleaved: WebGPU has a hard limit of 8 vertex buffers per pipeline.
 * As ten standalone InstancedBufferAttributes the particle fields alone blew
 * the limit for the mesh/procMesh family (geometry position/uv/normal count
 * too), so those pipelines failed to build and the layers rendered nothing on
 * WebGPU — including Android Chrome. One InstancedInterleavedBuffer is a
 * single slot regardless of field count, and one contiguous upload per frame
 * instead of ten small ones (the mobile-friendly shape anyway). WebGL2
 * binds interleaved attributes natively via stride/offset.
 */
export const PARTICLE_STRIDE = 20;
/** Float offsets of each field within a particle's PARTICLE_STRIDE record. */
export const P_CENTER = 0; // vec3, sim space
export const P_VELOCITY = 3; // vec3, sim-owned (stretched billboards read it)
export const P_BIRTH = 6; // float, instance-local seconds; −1 = never spawned
export const P_LIFETIME = 7; // float, seconds
export const P_SEED = 8; // vec4 random per particle (tumble axes, shader variation)
export const P_SIZE = 12; // float, world units
export const P_ROTATION = 13; // float, radians
export const P_ANGVEL = 14; // float, radians/sec
export const P_STRETCH = 15; // float, stretched-billboard factor (birth provider)
export const P_TINT = 16; // vec4 rgba
export function createParticleAttributes(capacity) {
    const array = new Float32Array(capacity * PARTICLE_STRIDE);
    for (let i = 0; i < capacity; i++)
        array[i * PARTICLE_STRIDE + P_BIRTH] = -1;
    const buffer = new InstancedInterleavedBuffer(array, PARTICLE_STRIDE);
    buffer.setUsage(DynamicDrawUsage);
    return { capacity, array, buffer };
}
/** Read one particle's center — tests and debugging. */
export function particleCenter(attrs, i) {
    const p = i * PARTICLE_STRIDE + P_CENTER;
    return [attrs.array[p], attrs.array[p + 1], attrs.array[p + 2]];
}
// ---------------------------------------------------------------------------
// shared pieces
// ---------------------------------------------------------------------------
/** v rotated by `angle` around unit axis `k` (Rodrigues). */
function rotateAxisAngle(v, k, angle) {
    const c = angle.cos();
    const s = angle.sin();
    return v.mul(c).add(cross(k, v).mul(s)).add(k.mul(dot(k, v)).mul(c.oneMinus()));
}
function buildCommon(opts) {
    const { timeUniform, attrs, atlas, rows } = opts;
    // every field reads from the ONE interleaved buffer — a single vertex-buffer
    // slot on WebGPU no matter how many fields a renderer consumes
    const field = (type, offset) => instancedDynamicBufferAttribute(attrs.buffer, type, PARTICLE_STRIDE, offset);
    const center = field('vec3', P_CENTER);
    const velocity = field('vec3', P_VELOCITY);
    const birth = field('float', P_BIRTH);
    const life = field('float', P_LIFETIME);
    const seed = field('vec4', P_SEED);
    const size0 = field('float', P_SIZE);
    const rot0 = field('float', P_ROTATION);
    const angVel = field('float', P_ANGVEL);
    const stretch0 = field('float', P_STRETCH);
    const tint = field('vec4', P_TINT);
    const nAge = normalizedAge(timeUniform, birth, life);
    const alive = aliveFactor(timeUniform, birth, life);
    const ageSec = timeUniform.sub(birth).max(0);
    let scale = size0.mul(alive);
    if (rows.size !== undefined)
        scale = scale.mul(sampleCurve(atlas.texture, rows.size, atlas.rows, nAge));
    let rotation = rot0.add(angVel.mul(ageSec));
    if (rows.rotation !== undefined) {
        const fullScaleRad = ((rows.rotationScale ?? 360) * Math.PI) / 180;
        rotation = rotation.add(sampleCurve(atlas.texture, rows.rotation, atlas.rows, nAge).mul(fullScaleRad));
    }
    return { center, velocity, seed, size0, rot0, angVel, stretch0, tint, nAge, alive, ageSec, scale, rotation };
}
/**
 * Fragment pipeline shared by every renderer: tint × (graph | gradient) ×
 * sprite/flipbook sample, then dissolve. `spriteMask` controls how the
 * `procedural` sprite is shaped: 'radial' for billboards, 'cross' for beam
 * ribbons (soft edges across uv.y), 'none' for meshes — a mesh IS the shape.
 */
function buildRgbAlpha(layer, opts, common, spriteMask) {
    const { timeUniform, atlas, rows } = opts;
    const { nAge, ageSec, seed, tint } = common;
    const mat = layer.material;
    const features = mat.features ?? {};
    // --- UV pipeline: beam tiling → scroll → flipbook cell mapping -------------
    let uvBase = uv();
    if (layer.renderer.type === 'beam' && (layer.renderer.tiles ?? 1) !== 1) {
        // repeat the sprite along the beam (Roblox TextureLength); fract keeps
        // clamped textures sampling inside [0,1] per tile
        uvBase = vec2(fract(uvBase.x.mul(layer.renderer.tiles ?? 1)), uvBase.y);
    }
    if (features.uvScroll && (features.uvScroll.x !== 0 || features.uvScroll.y !== 0)) {
        uvBase = fract(uvBase.add(vec2(features.uvScroll.x, features.uvScroll.y).mul(timeUniform)));
    }
    let uvA = uvBase;
    let uvB = null;
    let frameMix = null;
    const renderer = layer.renderer;
    if (renderer.type === 'flipbook') {
        const cols = Math.max(1, renderer.cols);
        const rowsN = Math.max(1, renderer.rows);
        const frames = Math.max(1, Math.min(renderer.frameCount ?? cols * rowsN, cols * rowsN));
        const f = renderer.playback.mode === 'lifetime'
            ? nAge.mul(frames - 1e-4)
            : ageSec.mul(renderer.playback.fps);
        const cellUv = (frameIndex) => {
            const fi = frameIndex.floor().mod(frames);
            const col = fi.mod(cols);
            const row = fi.div(cols).floor();
            // sheet row 0 is the TOP of the image; uv v runs bottom-up
            return vec2(uvBase.x.add(col).div(cols), uvBase.y.add(float(rowsN - 1).sub(row)).div(rowsN));
        };
        uvA = cellUv(f);
        if (renderer.blendFrames) {
            uvB = cellUv(f.add(1));
            frameMix = f.fract();
        }
    }
    // --- base color: tint × (graph | gradient) --------------------------------
    let rgb = tint.xyz;
    let alpha = tint.w;
    if (mat.graph) {
        const out = compileGraph(mat.graph, {
            nAge,
            seed,
            uvNode: uvA,
            time: timeUniform,
            atlas,
            graphRows: rows.graphRows ?? {},
        });
        rgb = rgb.mul(out.rgb);
        alpha = alpha.mul(out.alpha);
    }
    else if (rows.color !== undefined) {
        const g = sampleGradient(atlas.texture, rows.color, atlas.rows, nAge);
        rgb = rgb.mul(g.xyz);
        alpha = alpha.mul(g.w);
    }
    // --- sprite/flipbook sample -------------------------------------------------
    const spriteRef = mat.sprite;
    if (spriteRef.type === 'procedural') {
        if (spriteMask === 'radial')
            alpha = alpha.mul(radialFalloff(spriteRef.hardness));
        else if (spriteMask === 'cross')
            alpha = alpha.mul(crossFalloff(spriteRef.hardness));
    }
    else {
        const spriteTex = resolveSpriteTexture(spriteRef);
        if (spriteTex) {
            let s = texture(spriteTex, uvA);
            if (uvB && frameMix)
                s = mix(s, texture(spriteTex, uvB), frameMix);
            rgb = rgb.mul(s.xyz);
            alpha = alpha.mul(s.w);
        }
    }
    // --- dissolve with colored edge rim ----------------------------------------
    if (features.dissolve && rows.dissolve !== undefined) {
        const d = features.dissolve;
        const noise = texture(builtinSprite('noise'), fract(uvBase.mul(d.noiseScale ?? 1))).x;
        const th = sampleCurve(atlas.texture, rows.dissolve, atlas.rows, nAge);
        const keep = step(th, noise);
        const edge = keep.sub(step(th.add(Math.max(1e-3, d.edgeWidth)), noise)).mul(step(1e-3, th));
        rgb = mix(rgb, vec3(d.edgeColor[0], d.edgeColor[1], d.edgeColor[2]), edge.mul(d.edgeColor[3]));
        alpha = alpha.mul(keep);
    }
    return { rgb, alpha };
}
function applyBlend(matObj, layer) {
    matObj.transparent = true;
    matObj.depthWrite = false;
    matObj.blending = layer.material.blend === 'additive' ? AdditiveBlending : NormalBlending;
}
// ---------------------------------------------------------------------------
// billboard family: billboard | stretchedBillboard | flipbook
// ---------------------------------------------------------------------------
export function buildBillboardMaterial(layer, opts) {
    const mat = new SpriteNodeMaterial();
    applyBlend(mat, layer);
    const c = buildCommon(opts);
    let scale = c.scale;
    let rotation = c.rotation;
    if (layer.renderer.type === 'stretchedBillboard') {
        // roll the quad to the screen-space velocity and lengthen with speed
        const vView = modelViewMatrix.mul(vec4(c.velocity, 0)).xyz;
        const speed = c.velocity.length();
        const len = c.scale.mul(speed.mul(c.stretch0).max(1));
        scale = vec2(len, c.scale);
        rotation = vView.y.atan(vView.x);
    }
    billboard(mat, { center: c.center, scale, rotation });
    const { rgb, alpha } = buildRgbAlpha(layer, opts, c, 'radial');
    mat.colorNode = rgb;
    mat.opacityNode = alpha;
    return mat;
}
// ---------------------------------------------------------------------------
// mesh family: mesh | procMesh (InstancedMesh with identity matrices;
// position/rotation/scale computed in TSL from the particle attributes)
// ---------------------------------------------------------------------------
export function buildMeshMaterial(layer, opts) {
    const mat = new MeshBasicNodeMaterial();
    applyBlend(mat, layer);
    mat.side = DoubleSide;
    const c = buildCommon(opts);
    const renderer = layer.renderer;
    const pgeo = positionGeometry; // typed swizzles fall over on geometry attributes
    if (renderer.type === 'procMesh' && renderer.shape.kind === 'ring') {
        // annulus expansion: radius from the size curve, rim width from the aux curve
        const dir = vec2(pgeo.x, pgeo.z);
        const angle = c.rotation;
        const rot = vec2(dir.x.mul(angle.cos()).sub(dir.y.mul(angle.sin())), dir.x.mul(angle.sin()).add(dir.y.mul(angle.cos())));
        const width = opts.rows.aux !== undefined
            ? sampleCurve(opts.atlas.texture, opts.rows.aux, opts.atlas.rows, c.nAge).mul(renderer.shape.widthScale)
            : float(renderer.shape.widthScale);
        const radial = c.scale.add(uv().y.sub(0.5).mul(width).mul(c.alive));
        mat.positionNode = c.center.add(vec3(rot.x.mul(radial), 0, rot.y.mul(radial)));
    }
    else {
        // generic instanced mesh: tumble for debris meshes, Y-spin for flat proc shapes
        const axis = renderer.type === 'mesh'
            ? c.seed.xyz.mul(2).sub(1).normalize()
            : vec3(0, 1, 0);
        const rotated = rotateAxisAngle(pgeo.mul(c.scale), axis, c.rotation);
        mat.positionNode = c.center.add(rotated);
    }
    const { rgb, alpha } = buildRgbAlpha(layer, opts, c, 'none');
    mat.colorNode = rgb;
    mat.opacityNode = alpha;
    return mat;
}
// ---------------------------------------------------------------------------
// beam renderer (Roblox-Beam equivalent): instanced parameter strips swept
// along a cubic Bézier in the vertex stage. positionGeometry carries
// (t, side, 0); real positions are computed here from the particle center +
// the def's end/curve offsets, then expanded camera-facing like a trail.
// ---------------------------------------------------------------------------
export function buildBeamMaterial(layer, opts) {
    const mat = new MeshBasicNodeMaterial();
    applyBlend(mat, layer);
    mat.side = DoubleSide;
    const c = buildCommon(opts);
    const r = layer.renderer;
    const t = positionGeometry.x;
    const side = positionGeometry.y;
    // Bézier control offsets from the start point (P0 = particle center):
    // straight thirds by default, bowed by curve0/curve1 (Roblox CurveSize0/1).
    const c0 = r.curve0 ?? [0, 0, 0];
    const c1 = r.curve1 ?? [0, 0, 0];
    const a1 = vec3(r.end[0] / 3 + c0[0], r.end[1] / 3 + c0[1], r.end[2] / 3 + c0[2]).mul(c.scale);
    const a2 = vec3((r.end[0] * 2) / 3 + c1[0], (r.end[1] * 2) / 3 + c1[1], (r.end[2] * 2) / 3 + c1[2]).mul(c.scale);
    const a3 = vec3(r.end[0], r.end[1], r.end[2]).mul(c.scale);
    const u = t.oneMinus();
    // B(t) − P0 = 3u²t·a1 + 3ut²·a2 + t³·a3
    const along = a1
        .mul(u.mul(u).mul(t).mul(3))
        .add(a2.mul(u.mul(t).mul(t).mul(3)))
        .add(a3.mul(t.mul(t).mul(t)));
    // B'(t) = 3u²·a1 + 6ut·(a2−a1) + 3t²·(a3−a2)
    const tangent = a1
        .mul(u.mul(u).mul(3))
        .add(a2.sub(a1).mul(u.mul(t).mul(6)))
        .add(a3.sub(a2).mul(t.mul(t).mul(3)));
    // Camera-facing expansion (same approximation as trails: exact for
    // translation-only instance roots).
    const spine = c.center.add(along);
    const worldPos = modelWorldMatrix.mul(vec4(spine, 1)).xyz;
    const viewDir = cameraPosition.sub(worldPos).normalize();
    const offsetDir = cross(viewDir, tangent.normalize().add(vec3(1e-5, 0, 0))).normalize();
    let width = float(r.widthScale).mul(c.alive);
    if (opts.rows.aux !== undefined) {
        width = width.mul(sampleCurve(opts.atlas.texture, opts.rows.aux, opts.atlas.rows, t));
    }
    mat.positionNode = spine.add(offsetDir.mul(side).mul(width.mul(0.5)));
    const { rgb, alpha } = buildRgbAlpha(layer, opts, c, 'cross');
    mat.colorNode = rgb;
    mat.opacityNode = alpha;
    return mat;
}
// ---------------------------------------------------------------------------
// trail renderer: one dynamic ribbon geometry per layer (non-instanced).
// Vertex attributes are written by TrailBuffers each frame; the vertex stage
// expands each history point into a camera-facing strip.
// ---------------------------------------------------------------------------
export function buildTrailMaterial(layer, opts) {
    const mat = new MeshBasicNodeMaterial();
    applyBlend(mat, layer);
    mat.side = DoubleSide;
    const { atlas, rows } = opts;
    const renderer = layer.renderer;
    const tangent = attribute('trailTangent', 'vec3');
    const side = attribute('trailSide', 'float');
    const info = attribute('trailInfo', 'vec3'); // (trailT, nAge, alive)
    const tintA = attribute('trailTint', 'vec4');
    const trailT = info.x;
    const nAge = info.y;
    const alive = info.z;
    // Camera-facing expansion. Computed against the world camera; exact when the
    // instance root carries translation only (rotation-heavy roots skew slightly).
    const worldPos = modelWorldMatrix.mul(vec4(positionGeometry, 1)).xyz;
    const viewDir = cameraPosition.sub(worldPos).normalize();
    const offsetDir = cross(viewDir, tangent.normalize()).normalize();
    let width = float(renderer.widthScale).mul(alive);
    if (rows.aux !== undefined) {
        width = width.mul(sampleCurve(atlas.texture, rows.aux, atlas.rows, trailT));
    }
    mat.positionNode = positionGeometry.add(offsetDir.mul(side).mul(width.mul(0.5)));
    let rgb = tintA.xyz;
    let alpha = tintA.w.mul(trailT.oneMinus()); // fade toward the tail
    if (rows.color !== undefined) {
        const g = sampleGradient(atlas.texture, rows.color, atlas.rows, nAge);
        rgb = rgb.mul(g.xyz);
        alpha = alpha.mul(g.w);
    }
    mat.colorNode = rgb;
    mat.opacityNode = alpha.mul(alive);
    return mat;
}
